import type { Params } from '@angular/router';
import type { Observable } from 'rxjs';

export enum GalleryItemType {
  PICTURE,
  VIDEO,
  FILE,
  PDF,
}

/**
 * Content shown in a gallery tile's hover-reveal panel.
 */
export interface GalleryItemInteractiveContent {
  readonly title: string;
  readonly subtitle?: string;
  readonly ctaLabel: string;
  readonly ctaLink: string;
  readonly ctaQueryParams?: Params;
  readonly ctaTrackingLabel: string;
}

export interface GalleryItem {
  /**
   * Identifies the item across updates, e.g. while it goes from `loading` to loaded. Must be unique within the gallery.
   * By default, items are told apart by position or URL.
   */
  readonly id?: string;
  readonly alt?: string;
  readonly caption?: string;
  readonly fileName: string;
  readonly source: string;
  readonly link?: string;
  readonly linkLabel?: string;
  readonly queryParams?: Params;
  readonly trackingLabel?: string;
  readonly isExternal?: boolean;
  readonly type: GalleryItemType;
  /** Only rendered when `fl-gallery-mosaic` sets `[interactiveContent]="true"`. */
  readonly interactiveContent?: GalleryItemInteractiveContent;
  /** Thumbnail URL source of the gallery item in the media viewer. By default, the source will be used. */
  readonly thumbnailSource?: string;
  /**
   * Download URL of the gallery item in the media viewer. By default, the source will be used.
   * Pass `false` to disable download button.
   */
  readonly downloadUrl?: string | false;
  /** Intrinsic width of the gallery item. */
  readonly width?: number;
  /** Intrinsic height of the gallery item. */
  readonly height?: number;
  readonly uploadDetails?: GalleryItemUploadDetails;
  /** The file isn't ready yet, so show a placeholder in the meantime (e.g. blurhash or spinner). */
  readonly loading?: boolean;
  /** The file couldn't be loaded, so it's shown as failed instead of `source`. */
  readonly loadFailed?: boolean;

  /** To be inplemented in the showcase card */
  readonly showcaseTitle?: string;
  readonly showcaseSubtitle?: string;
  readonly showcaseLink?: string;
  readonly blurHash?: string;
  /** Background color (any CSS color value) shown as an overlay on hover */
  readonly hoverOverlayColor?: string;
  /** Text shown at the bottom-left of the image on hover */
  readonly hoverOverlayText?: string;
}

export interface GalleryItemUploadDetails {
  progress$: Observable<number>;
  done: boolean;
}
